import { Link } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import { inventarioService } from '../services/api';
import ModalLlaveAcceso from '../components/ModalLlaveAcceso';
import { descargarBlob } from '../utils/descargar';
import { Download, KeyRound } from 'lucide-react';

function fmt(n: any) {
  return parseFloat(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const card: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)' };
const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 6 };
const inputStyle: React.CSSProperties = { width: '100%', height: 38, border: '1px solid var(--line-strong)', borderRadius: 8, padding: '0 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
const th: React.CSSProperties = { padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', textAlign: 'left' };
const td: React.CSSProperties = { padding: '10px 16px', fontSize: 13, color: 'var(--ink-2)', borderTop: '1px solid var(--line-2)' };

export default function Mermas() {
  const hoy = new Date().toISOString().slice(0, 10);
  const [tab, setTab] = useState<'registrar' | 'reporte' | 'rendimiento'>('registrar');

  // Pruebas de rendimiento capturadas desde la app móvil (migración 026)
  const [pruebas, setPruebas] = useState<any[]>([]);
  const [cargandoPruebas, setCargandoPruebas] = useState(false);
  const [pri, setPri] = useState('');
  const [prf, setPrf] = useState('');

  const [insumos, setInsumos] = useState<any[]>([]);
  const [form, setForm] = useState({ insumo_id: '', cantidad: '', motivo: '' });
  const [guardando, setGuardando] = useState(false);
  const [ok, setOk] = useState('');
  const [error, setError] = useState('');
  const [ultimasMermas, setUltimasMermas] = useState<any[]>([]);
  const [llaveMerma, setLlaveMerma] = useState(false);

  // Dar entrada al stock de las porciones de una prueba: el insumo porcionado
  // es otro producto distinto de la pieza cruda, por eso se elige aquí.
  const [porcionPrueba, setPorcionPrueba] = useState<any>(null);
  const [porcionInsumoId, setPorcionInsumoId] = useState('');
  const [porcionGuardando, setPorcionGuardando] = useState(false);

  const [fi, setFi] = useState(hoy);
  const [ff, setFf] = useState(hoy);
  const [resumen, setResumen] = useState<any>(null);
  const [cargandoReporte, setCargandoReporte] = useState(false);

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    const [ins, mermas] = await Promise.all([
      inventarioService.getInsumos(),
      inventarioService.getMermas(),
    ]);
    setInsumos(ins.data);
    setUltimasMermas(mermas.data.slice(0, 10));
  };

  const registrar = async () => {
    setGuardando(true); setError(''); setOk('');
    try {
      await inventarioService.crearMerma({ insumo_id: form.insumo_id, cantidad: parseFloat(form.cantidad), motivo: form.motivo });
      setOk('Merma registrada correctamente');
      setForm({ insumo_id: '', cantidad: '', motivo: '' });
      cargar();
      setTimeout(() => setOk(''), 3000);
    } catch (e: any) { setError(e.response?.data?.error || 'Error al registrar'); }
    finally { setGuardando(false); }
  };

  const intentarRegistrar = () => {
    if (!form.insumo_id || !form.cantidad || !form.motivo) { setError('Todos los campos son requeridos'); return; }
    setError(''); setLlaveMerma(true);
  };

  const consultarReporte = async () => {
    setCargandoReporte(true);
    try { const res = await inventarioService.getResumenMermas(fi, ff); setResumen(res.data); }
    catch (e: any) { setError(e.response?.data?.error || 'Error al consultar'); }
    finally { setCargandoReporte(false); }
  };

  const cargarPruebas = async () => {
    setCargandoPruebas(true);
    try {
      const res = await inventarioService.getPruebasRendimiento(
        pri && prf ? pri : undefined, pri && prf ? prf : undefined
      );
      setPruebas(res.data);
    } catch (e: any) { setError(e.response?.data?.error || 'Error al consultar'); }
    finally { setCargandoPruebas(false); }
  };

  // Entrar a la pestaña o cambiar una fecha YA consulta — mismo criterio que
  // Corte y Reportes: sin botón "Consultar" intermedio.
  useEffect(() => { if (tab === 'reporte') consultarReporte(); /* eslint-disable-next-line */ }, [tab, fi, ff]);
  useEffect(() => { if (tab === 'rendimiento') cargarPruebas(); /* eslint-disable-next-line */ }, [tab, pri, prf]);

  const agregarPorciones = async () => {
    if (!porcionPrueba || !porcionInsumoId) return;
    setPorcionGuardando(true); setError(''); setOk('');
    try {
      await inventarioService.registrarMovimiento(porcionInsumoId, {
        tipo: 'entrada',
        cantidad: Number(porcionPrueba.porciones),
        motivo: `Porciones de prueba de rendimiento — ${porcionPrueba.insumo_texto}`,
      });
      const nombre = insumos.find(i => i.id === porcionInsumoId)?.nombre || 'el insumo';
      setOk(`${porcionPrueba.porciones} porciones entraron al stock de ${nombre}`);
      setPorcionPrueba(null); setPorcionInsumoId('');
      cargar();
      setTimeout(() => setOk(''), 4000);
    } catch (e: any) { setError(e.response?.data?.error || 'No se pudieron agregar las porciones'); }
    finally { setPorcionGuardando(false); }
  };

  const rendimientoPct = (p: any) => {
    const ini = parseFloat(p.peso_inicial || 0);
    return ini > 0 ? (parseFloat(p.peso_neto || 0) / ini) * 100 : 0;
  };

  const exportarPruebasCSV = () => {
    if (pruebas.length === 0) return;
    const filas = [
      ['Fecha', 'Insumo', 'Proveedor', 'Peso inicial', 'Peso bolsa', 'Peso merma', 'Peso neto', 'Rendimiento %', 'Porciones', 'Peso por porción', 'Costo merma', 'Notas', 'Usuario'],
      ...pruebas.map(p => [
        new Date(p.created_at).toLocaleString('es-MX'),
        p.insumo_texto, p.proveedor_texto || '',
        p.peso_inicial, p.peso_bolsa, p.peso_merma, p.peso_neto,
        rendimientoPct(p).toFixed(1), p.porciones ?? '', p.peso_por_porcion ?? '',
        fmt(p.costo_estimado), (p.notas || '').replace(/[\r\n,]+/g, ' '), p.usuario || '',
      ]),
    ];
    const csv = filas.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    descargarBlob(blob, `pruebas-rendimiento-${pri || 'todas'}-${prf || ''}.csv`);
  };

  const exportarCSV = () => {
    if (!resumen) return;
    const filas = [
      ['Insumo', 'Unidad', 'Cantidad total', 'Costo estimado', 'Registros'],
      ...resumen.resumen.map((r: any) => [r.insumo, r.unidad, r.total_cantidad, fmt(r.total_costo), r.num_registros]),
      [],
      ['Total', '', fmt(resumen.totales.total_cantidad), fmt(resumen.totales.total_costo), ''],
    ];
    const csv = filas.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    descargarBlob(blob, `mermas-${fi}-${ff}.csv`);
  };

  return (
    <div style={{ maxWidth: tab === 'rendimiento' ? 1100 : 760 }}>
      <div style={{ marginBottom: 20 }}>
        <Link to="/inventario" style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', textDecoration: 'none' }}>← Inventario</Link>
        <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.02em', color: 'var(--text)', margin: 0 }}>Mermas y desperdicios</h1>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '2px 0 0' }}>Registro y reporte de pérdidas de inventario</p>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'var(--well)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {([['registrar', 'Registrar merma'], ['reporte', 'Reporte'], ['rendimiento', 'Pruebas de rendimiento']] as const).map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: tab === t ? 'var(--surface)' : 'transparent', color: tab === t ? 'var(--brand-texto)' : 'var(--muted)', boxShadow: tab === t ? '0 1px 2px rgba(0,0,0,.08)' : 'none' }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'registrar' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ ...card, padding: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Nueva merma</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>Insumo *</label>
                <select value={form.insumo_id} onChange={e => setForm(f => ({ ...f, insumo_id: e.target.value }))} style={{ ...inputStyle, background: 'var(--surface)', cursor: 'pointer' }}>
                  <option value="">Seleccionar insumo</option>
                  {insumos.map(i => <option key={i.id} value={i.id}>{i.nombre} — Stock: {parseFloat(i.stock_actual).toFixed(2)} {i.unidad}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Cantidad *</label>
                  <input type="number" min="0" step="0.001" value={form.cantidad} onChange={e => setForm(f => ({ ...f, cantidad: e.target.value }))} placeholder="0.000" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Motivo *</label>
                  <input value={form.motivo} onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))} placeholder="Ej. Caducidad, rotura" style={inputStyle} />
                </div>
              </div>
              {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--brand-texto)', fontSize: 13, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--danger-line)' }}>{error}</div>}
              {ok && <div style={{ background: 'var(--ok-bg)', color: 'var(--ok)', fontSize: 13, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--ok-line)' }}>{ok}</div>}
              <button onClick={intentarRegistrar} disabled={guardando}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 40, border: 'none', borderRadius: 8, background: 'var(--brand)', color: 'var(--on-brand)', fontSize: 13, fontWeight: 600, cursor: guardando ? 'not-allowed' : 'pointer', opacity: guardando ? .7 : 1 }}>
                <KeyRound size={14} /> {guardando ? 'Registrando…' : 'Registrar merma'}
              </button>
            </div>
          </div>

          {ultimasMermas.length > 0 && (
            <div style={{ ...card, overflowX: 'auto' }}>
              <div style={{ fontWeight: 700, fontSize: 14, padding: '16px 20px 0' }}>Últimas mermas registradas</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                <thead style={{ background: 'var(--bg)' }}>
                  <tr>
                    <th style={th}>Insumo</th>
                    <th style={th}>Cantidad</th>
                    <th style={th}>Motivo</th>
                    <th style={{ ...th, textAlign: 'right' }}>Costo</th>
                    <th style={{ ...th, textAlign: 'right' }}>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {ultimasMermas.map(m => (
                    <tr key={m.id}>
                      <td style={td}>{m.insumo}</td>
                      <td style={{ ...td, color: 'var(--muted)' }}>{m.cantidad} {m.unidad}</td>
                      <td style={{ ...td, color: 'var(--muted)', fontSize: 12 }}>{m.motivo}</td>
                      <td style={{ ...td, textAlign: 'right', color: 'var(--brand-texto)', fontWeight: 600 }}>${fmt(m.costo_estimado)}</td>
                      <td style={{ ...td, textAlign: 'right', color: 'var(--faint)', fontSize: 12 }}>{new Date(m.created_at).toLocaleDateString('es-MX')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'reporte' && (
        <div>
          <div style={{ ...card, padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {[{ label: 'Desde', value: fi, set: setFi, max: ff }, { label: 'Hasta', value: ff, set: setFf, max: hoy }].map(({ label, value, set, max }) => (
              <div key={label}>
                <label style={labelStyle}>{label}</label>
                <input type="date" value={value} max={max} onChange={e => set(e.target.value)} style={{ ...inputStyle, height: 36 }} />
              </div>
            ))}
            {cargandoReporte && <span style={{ fontSize: 12, color: 'var(--faint)', paddingBottom: 10 }}>Actualizando…</span>}
          </div>

          {resumen && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                <div style={{ ...card, padding: 16 }}>
                  <p style={{ fontSize: 12, color: 'var(--faint)', margin: 0 }}>Costo total en mermas</p>
                  <p style={{ fontSize: 24, fontWeight: 700, color: 'var(--brand-texto)', margin: '4px 0 0' }}>${fmt(resumen.totales.total_costo)}</p>
                </div>
                <div style={{ ...card, padding: 16 }}>
                  <p style={{ fontSize: 12, color: 'var(--faint)', margin: 0 }}>Insumos afectados</p>
                  <p style={{ fontSize: 24, fontWeight: 700, margin: '4px 0 0' }}>{resumen.resumen.length}</p>
                </div>
              </div>

              <div style={{ ...card, overflowX: 'auto', marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: 'var(--bg)' }}>
                    <tr>
                      <th style={th}>Insumo</th>
                      <th style={{ ...th, textAlign: 'center' }}>Cantidad perdida</th>
                      <th style={{ ...th, textAlign: 'center' }}>Registros</th>
                      <th style={{ ...th, textAlign: 'right' }}>Costo estimado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumen.resumen.map((r: any) => (
                      <tr key={r.insumo}>
                        <td style={{ ...td, fontWeight: 500 }}>{r.insumo}</td>
                        <td style={{ ...td, textAlign: 'center', color: 'var(--muted)' }}>{fmt(r.total_cantidad)} {r.unidad}</td>
                        <td style={{ ...td, textAlign: 'center', color: 'var(--muted)' }}>{r.num_registros}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: 'var(--brand-texto)' }}>${fmt(r.total_costo)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: 'var(--bg)' }}>
                      <td style={{ ...td, fontWeight: 700 }} colSpan={3}>Total</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: 'var(--brand-texto)' }}>${fmt(resumen.totales.total_costo)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <button onClick={exportarCSV} style={{ display: 'flex', alignItems: 'center', gap: 6, height: 38, padding: '0 16px', border: '1px solid var(--ok)', color: 'var(--ok)', background: 'var(--surface)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <Download size={14} /> Exportar CSV
              </button>
            </>
          )}
        </div>
      )}

      {tab === 'rendimiento' && (
        <div>
          <div style={{ ...card, padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {[{ label: 'Desde', value: pri, set: setPri }, { label: 'Hasta', value: prf, set: setPrf }].map(({ label, value, set }) => (
              <div key={label}>
                <label style={labelStyle}>{label}</label>
                <input type="date" value={value} max={hoy} onChange={e => set(e.target.value)} style={{ ...inputStyle, height: 36 }} />
              </div>
            ))}
            <span style={{ fontSize: 12, color: 'var(--faint)', paddingBottom: 10 }}>
              {cargandoPruebas ? 'Actualizando…' : 'Sin fechas muestra todas las pruebas'}
            </span>
          </div>

          {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--brand-texto)', fontSize: 13, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--danger-line)', marginBottom: 12 }}>{error}</div>}
          {ok && <div style={{ background: 'var(--ok-bg)', color: 'var(--ok)', fontSize: 13, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--ok-line)', marginBottom: 12 }}>{ok}</div>}

          {pruebas.length === 0 ? (
            <div style={{ ...card, padding: 24, textAlign: 'center', color: 'var(--faint)', fontSize: 13 }}>
              {cargandoPruebas ? 'Cargando…' : 'No hay pruebas de rendimiento registradas en este rango'}
            </div>
          ) : (
            <>
              <div style={{ ...card, overflowX: 'auto', marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                  <thead style={{ background: 'var(--bg)' }}>
                    <tr>
                      <th style={th}>Fecha</th>
                      <th style={th}>Insumo</th>
                      <th style={th}>Proveedor</th>
                      <th style={{ ...th, textAlign: 'right' }}>Bruto</th>
                      <th style={{ ...th, textAlign: 'right' }}>Bolsa</th>
                      <th style={{ ...th, textAlign: 'right' }}>Merma</th>
                      <th style={{ ...th, textAlign: 'right' }}>Neto</th>
                      <th style={{ ...th, textAlign: 'center' }}>Rend.</th>
                      <th style={{ ...th, textAlign: 'center' }}>Porciones</th>
                      <th style={{ ...th, textAlign: 'right' }}>Costo merma</th>
                      <th style={{ ...th, textAlign: 'center' }}>Porciones al stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pruebas.map(p => (
                      <Fragment key={p.id}>
                      <tr>
                        <td style={{ ...td, color: 'var(--faint)', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {new Date(p.created_at).toLocaleDateString('es-MX')}
                        </td>
                        <td style={{ ...td, fontWeight: 500 }}>
                          {p.insumo_texto}
                          {p.notas && <div style={{ fontSize: 11, color: 'var(--faint)', fontStyle: 'italic' }}>{p.notas}</div>}
                        </td>
                        <td style={{ ...td, color: 'var(--muted)' }}>{p.proveedor_texto || '—'}</td>
                        <td style={{ ...td, textAlign: 'right', color: 'var(--muted)' }}>{fmt(p.peso_inicial)}</td>
                        <td style={{ ...td, textAlign: 'right', color: 'var(--muted)' }}>{fmt(p.peso_bolsa)}</td>
                        <td style={{ ...td, textAlign: 'right', color: 'var(--brand-texto)' }}>{fmt(p.peso_merma)}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(p.peso_neto)} {p.unidad || ''}</td>
                        <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: rendimientoPct(p) >= 70 ? 'var(--ok)' : 'var(--warn)' }}>
                          {rendimientoPct(p).toFixed(0)}%
                        </td>
                        <td style={{ ...td, textAlign: 'center', color: 'var(--muted)' }}>
                          {p.porciones ? `${p.porciones} · ${fmt(p.peso_por_porcion)} c/u` : '—'}
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: 'var(--brand-texto)' }}>${fmt(p.costo_estimado)}</td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          {p.porciones ? (
                            <button
                              onClick={() => { setPorcionInsumoId(''); setPorcionPrueba(porcionPrueba?.id === p.id ? null : p); }}
                              style={{ height: 30, padding: '0 12px', border: '1px solid var(--ok)', color: 'var(--ok)', background: 'var(--surface)', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              {porcionPrueba?.id === p.id ? 'Cancelar' : 'Agregar'}
                            </button>
                          ) : <span style={{ color: 'var(--muted)' }}>—</span>}
                        </td>
                      </tr>
                      {porcionPrueba?.id === p.id && (
                        <tr>
                          <td colSpan={11} style={{ ...td, background: 'var(--bg)' }}>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                                Dar entrada de <b>{p.porciones}</b> porciones al stock de:
                              </span>
                              <select value={porcionInsumoId} onChange={e => setPorcionInsumoId(e.target.value)}
                                style={{ ...inputStyle, width: 'auto', minWidth: 280, height: 34, background: 'var(--surface)', cursor: 'pointer' }}>
                                <option value="">Seleccionar insumo porcionado</option>
                                {insumos.map(i => <option key={i.id} value={i.id}>{i.nombre} — Stock: {parseFloat(i.stock_actual).toFixed(2)} {i.unidad}</option>)}
                              </select>
                              <button onClick={agregarPorciones} disabled={!porcionInsumoId || porcionGuardando}
                                style={{ height: 34, padding: '0 16px', border: 'none', borderRadius: 8, background: 'var(--ok)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: !porcionInsumoId || porcionGuardando ? 'not-allowed' : 'pointer', opacity: !porcionInsumoId || porcionGuardando ? .6 : 1 }}>
                                {porcionGuardando ? 'Agregando…' : 'Agregar al stock'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <button onClick={exportarPruebasCSV} style={{ display: 'flex', alignItems: 'center', gap: 6, height: 38, padding: '0 16px', border: '1px solid var(--ok)', color: 'var(--ok)', background: 'var(--surface)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <Download size={14} /> Exportar CSV
              </button>
            </>
          )}
        </div>
      )}

      {llaveMerma && (
        <ModalLlaveAcceso
          permiso="puede_registrar_mermas"
          titulo="Registrar merma"
          descripcion="Esta acción requiere autorización de un usuario con permiso para registrar mermas."
          onSuccess={() => { setLlaveMerma(false); registrar(); }}
          onCancel={() => setLlaveMerma(false)}
        />
      )}
    </div>
  );
}
